// src/API/Address/ShareGet.ts
import { toast } from "react-toastify";
import { Address } from "./ViewAllAddedAddressByUserAsync";
import { getApiBaseUrl } from "../../utils/apiBaseUrl";

/**
 * Fetches address details using an encrypted ID (for shared links).
 *
 * @param pamCode - The PAM code (from URL path)
 * @param encryptedId - The encrypted ID (query param ?s=)
 * @param token - Optional authentication token
 * @param t - Translation function
 * @returns Promise<Address | null>
 */
export async function shareEncryptedId(
  pamCode: string,
  encryptedId: string,
  token: string | null,
  t: (key: string) => string
): Promise<Address | null> {
  if (!navigator.onLine) {
    toast.error(t("internetConnection"), { toastId: "offline-error" });
    return null;
  }

  try {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/api/Share/resolve/${pamCode}?s=${encodeURIComponent(
      encryptedId
    )}`;

    // console.log('🔗 ShareGet API URL:', url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      let errorMessage = t("failedToFetchAddress");
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
        console.error('❌ ShareGet API Error Response:', errorData);
      } catch {
        console.error('❌ ShareGet API Error - No JSON response');
      }
      console.error('❌ ShareGet API Failed - Status:', response.status, 'StatusText:', response.statusText);
      toast.error(errorMessage);
      return null;
    }

    const data: Address[] = await response.json();
    // console.log('📡 ShareGet API Response Data:', data);
    
    // Return the first address from the array, or null if empty
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    toast.error(t("somethingWentWrong"));
    console.error("Error fetching address:", error);
    return null;
  }
}
